import React, { useId, useRef, useState } from "react";
import HoverCard, { HoverCardPanel } from "../HoverCard";

// Focusable hover / tap targets laid over a chart (the game columns, the
// radar vertices, the form and consistency points). Shared by the charts in
// this folder; a card normally uses a chart, not this directly.
//
//   ChartTargets({ targets, renderDetail, phone, tap, label, shape, onFocusTarget, onBlurTarget, activeIndex })
//
// targets  [{ key, style, label, placement }]: `style` places the target
//          absolutely inside the chart box (percentages of the viewBox),
//          `label` is its full accessible name ("Jul 14 vs Team A: PTS 23"),
//          `placement` the HoverCard placement ("top", "bottom-start", ...).
// renderDetail(index) -> the detail body for that target.
// Desktop: hover or keyboard focus opens the detail in a HoverCard (a
//   HoverCardPanel), mouse leave, blur or Esc close it; a click opens it too
//   (a tablet tap focuses and clicks).
// Phone (`phone` + `tap`, a useTapPanel()): each target is a toggle button
//   (aria-pressed) for the chart's TapPanel, which the chart renders.
// One Tab stop for the whole set (roving tabindex): the arrow keys, Home
//   and End move between targets. `shape` "column" (a full-height slot) or
//   "point" (a 30px circle). `activeIndex` marks a target as picked.
// The targets sit in a role="group" named by `label`.

const cx = (...parts) => parts.filter(Boolean).join(" ");

const SHAPES = {
  column:
    "rounded-[6px] hover:bg-bm-ink/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bm-ink",
  point:
    "rounded-full hover:bg-bm-orange/10 hover:ring-2 hover:ring-bm-orange focus-visible:bg-bm-orange/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bm-orange",
};
const PICKED = { column: "bg-bm-ink/5", point: "bg-bm-orange/10 ring-2 ring-bm-orange" };

export const ChartTargets = ({
  targets = [],
  renderDetail,
  phone = false,
  tap,
  label,
  shape = "column",
  onFocusTarget,
  onBlurTarget,
  activeIndex = null,
}) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const buttons = useRef([]);
  const anchors = useRef({});
  const tipId = useId();
  // A stable ref object per target: HoverCard measures it once per open.
  const anchorRef = (i) => {
    if (!anchors.current[i]) anchors.current[i] = { current: null };
    return anchors.current[i];
  };

  const count = targets.length;
  if (!count) return null;
  const roving = Math.min(focusIndex, count - 1);

  const show = (i) => {
    if (!phone) setOpenIndex(i);
    onFocusTarget?.(i);
  };
  const hide = (i) => {
    if (!phone) setOpenIndex((current) => (current === i ? null : current));
    onBlurTarget?.(i);
  };

  const onKeyDown = (e, i) => {
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % count;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + count) % count;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = count - 1;
    else if (e.key === "Escape") {
      if (!phone) setOpenIndex(null);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    setFocusIndex(next);
    buttons.current[next]?.focus();
  };

  return (
    <div role="group" aria-label={label} className="pointer-events-none absolute inset-0">
      {targets.map((t, i) => {
        const open = !phone && openIndex === i;
        const picked = activeIndex === i || (phone && tap?.isOpen(i));
        const common = {
          ref: (el) => {
            buttons.current[i] = el;
          },
          tabIndex: i === roving ? 0 : -1,
          "aria-label": t.label,
          className: cx("pointer-events-auto absolute inset-0", SHAPES[shape] || SHAPES.column, picked && (PICKED[shape] || PICKED.column)),
        };
        const button = phone ? (
          <button
            {...common}
            {...(tap ? tap.triggerProps(i) : { type: "button" })}
            onFocus={() => {
              setFocusIndex(i);
              onFocusTarget?.(i);
            }}
            onBlur={() => onBlurTarget?.(i)}
            onKeyDown={(e) => {
              tap?.triggerProps(i).onKeyDown(e);
              onKeyDown(e, i);
            }}
          />
        ) : (
          <button
            {...common}
            type="button"
            aria-describedby={open ? `${tipId}-${i}` : undefined}
            onMouseEnter={() => show(i)}
            onMouseLeave={() => hide(i)}
            onFocus={() => {
              setFocusIndex(i);
              show(i);
            }}
            onBlur={() => hide(i)}
            onClick={() => show(i)}
            onKeyDown={(e) => onKeyDown(e, i)}
          />
        );
        return (
          <div key={t.key ?? i} ref={anchorRef(i)} className={cx("absolute", open ? "z-40" : "z-[1]")} style={t.style}>
            {button}
            {!phone && renderDetail && (
              <HoverCard anchorRef={anchorRef(i)} open={open} onClose={() => setOpenIndex(null)} placement={t.placement || "top"} id={`${tipId}-${i}`} as="div" arrow={false}>
                <HoverCardPanel>{open ? renderDetail(i) : null}</HoverCardPanel>
              </HoverCard>
            )}
          </div>
        );
      })}
    </div>
  );
};

// The HoverCard alignment for target i of n laid out left to right: the
// first three open to the right, the last three to the left, the rest
// centred (the mocks' game columns).
export const columnPlacement = (i, n, side = "top") => {
  if (n > 6 && i < 3) return `${side}-start`;
  if (n > 6 && i > n - 4) return `${side}-end`;
  return side;
};

export default ChartTargets;
